import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Bell, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";
import { API, fmt, fmtDate } from "./ledgerShared";

const BUCKETS = [["0-30", "0–30 days", "#166534"], ["31-60", "31–60 days", "#92400E"], ["61-90", "61–90 days", "#B45309"], ["90+", "90+ days", "#B91C1C"]];

/** Receivables ageing: open invoices by age, per client, plus milestones that are due but not yet invoiced. */
export default function ReceivablesTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/ledger/receivables`, { params: { include_archived: includeArchived ? 1 : 0 } }); setData(d); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load receivables"); }
    setLoading(false);
  }, [includeArchived]);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    if (!window.confirm("Send reminder e-mails now for every project that has reminders switched on?")) return;
    setSending(true);
    try { const { data: r } = await axios.post(`${API}/ledger/receivables/send-reminders`); toast.success(`Sent ${r.sent} reminder${r.sent === 1 ? "" : "s"}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setSending(false);
  };

  if (loading && !data) return <div className="flex items-center gap-2 text-[#64748B] text-sm"><RefreshCw size={14} className="animate-spin" /> Loading…</div>;
  if (!data) return null;
  const total = data.total_outstanding || 0;
  return (
    <div className="space-y-5" data-testid="receivables-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {BUCKETS.map(([k, label, color]) => (
          <div key={k} className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={`ageing-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</div>
            <div className="text-xl font-bold font-heading" style={{ color }}>{fmt(data.buckets[k]?.amount)}</div>
            <div className="text-[11px] text-[#94A3B8]">{data.buckets[k]?.count || 0} invoice{data.buckets[k]?.count === 1 ? "" : "s"}{total ? ` · ${Math.round((data.buckets[k]?.amount || 0) / total * 100)}%` : ""}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-[#0A1128]">Total outstanding <strong>{fmt(total)}</strong> across {data.invoices.length} invoice{data.invoices.length === 1 ? "" : "s"} · as of {fmtDate(data.as_of)}</span>
        <label className="flex items-center gap-1.5 text-xs text-[#64748B] ml-auto"><input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} /> include archived</label>
        {can(user, "receipts.send") && <button onClick={sendNow} disabled={sending} className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50 disabled:opacity-60"><Bell size={12} /> {sending ? "Sending…" : "Send reminders now"}</button>}
      </div>

      {data.due_unbilled?.length > 0 && (
        <div className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg px-4 py-3" data-testid="due-unbilled">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#92400E]"><AlertCircle size={14} /> {fmt(data.due_unbilled_total)} due but not yet invoiced</div>
          <ul className="mt-1 text-xs text-[#92400E] space-y-0.5">
            {data.due_unbilled.map(m => <li key={m.milestone_id}>{m.name} — {m.project_name} ({m.client_name}) · {fmt(m.amount)} · <Link to={`/app/projects/${m.project_id}/financials/pi?milestone=${m.milestone_id}`} className="underline font-semibold">raise PI</Link></li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#64748B]">
              <tr><th className="text-left px-4 py-2">Invoice</th><th className="text-left px-3 py-2">Client / project</th><th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">Outstanding</th><th className="text-right px-3 py-2">Age</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {data.invoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">Nothing outstanding.</td></tr>}
              {data.invoices.map(r => { const c = BUCKETS.find(b => b[0] === r.bucket)?.[2]; return (
                <tr key={r.pi_id} className="border-t border-[#F1F5F9]">
                  <td className="px-4 py-2.5 font-mono text-xs">{r.pi_number}</td>
                  <td className="px-3 py-2.5 text-xs"><div className="font-medium text-[#0A1128]">{r.client_name || "—"}</div><Link to={`/app/projects/${r.project_id}/financials/pi`} className="text-[#64748B] hover:underline">{r.project_name}</Link></td>
                  <td className="px-3 py-2.5 text-xs text-[#64748B]">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">{fmt(r.total)}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums font-semibold" style={{ color: c }}>{fmt(r.balance)}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums" style={{ color: c }}>{r.age_days} d</td>
                  <td className="px-3 py-2.5 text-right text-[10px] text-[#94A3B8] whitespace-nowrap">{r.reminders_enabled ? (r.reminder_count ? `${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""}${r.last_reminder_at ? " · " + fmtDate(r.last_reminder_at) : ""}` : "reminders on") : ""}</td>
                </tr>); })}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg">
          <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-sm text-[#0A1128]">By client</div>
          {data.by_client.length === 0 && <div className="px-4 py-6 text-xs text-[#94A3B8]">—</div>}
          {data.by_client.map(c => (
            <div key={c.client_name} className="px-4 py-2.5 border-b border-[#F1F5F9] flex items-center gap-2 text-xs">
              <div className="flex-1 min-w-0"><div className="font-medium text-[#0A1128] truncate">{c.client_name}</div><div className="text-[#94A3B8]">{c.count} invoice{c.count === 1 ? "" : "s"} · oldest {c.oldest_days} d</div></div>
              <div className="font-semibold tabular-nums" style={{ color: c.oldest_days > 90 ? "#B91C1C" : c.oldest_days > 60 ? "#B45309" : c.oldest_days > 30 ? "#92400E" : "#166534" }}>{fmt(c.balance)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
